import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Menu, X, Award, Heart, Crown, Globe } from 'lucide-react';
import { useI18n } from '@/i18n';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = useLocation();
  const { lang, setLang, t } = useI18n();

  const isActive = (path) => location.pathname === path;

  const navItem = (to, label, icon = null, testId = '') => (
    <Link to={to} onClick={() => setIsMenuOpen(false)}>
      <Button
        variant={isActive(to) ? 'default' : 'ghost'}
        className={`${
          isActive(to)
            ? 'bg-orange-500 hover:bg-orange-600 text-white'
            : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
        } font-medium transition-all duration-200`}
        data-testid={testId}
      >
        {icon}
        {label}
      </Button>
    </Link>
  );

  const languageSelector = (
    <Select value={lang} onValueChange={setLang}>
      <SelectTrigger
        className="w-auto min-w-[110px] border-orange-300 text-gray-700 font-medium"
        data-testid="lang-select"
      >
        <Globe className="h-4 w-4 mr-1 text-orange-600" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="en" data-testid="lang-en">English</SelectItem>
        <SelectItem value="fr" data-testid="lang-fr">Français</SelectItem>
        <SelectItem value="es" data-testid="lang-es">Español</SelectItem>
      </SelectContent>
    </Select>
  );

  return (
    <header className="bg-white border-b-4 border-orange-400 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group" data-testid="logo-link">
            <div className="bg-gradient-to-br from-orange-400 to-red-500 p-2.5 rounded-xl transform group-hover:scale-110 transition-transform duration-200">
              <Award className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Youth Baseball</h1>
              <p className="text-xs text-orange-600 font-medium">Skills & Drills</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navItem('/', t('home'), null, 'nav-home')}
            {navItem('/exercises', t('exercises'), null, 'nav-exercises')}
            {navItem('/favorites', t('favorites'), <Heart className="h-4 w-4 mr-1" />, 'nav-favorites')}
            <Link to="/pricing" onClick={() => setIsMenuOpen(false)}>
              <Button
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold transition-all duration-200 shadow-md hover:shadow-lg ml-2"
                data-testid="nav-pricing"
              >
                <Crown className="h-4 w-4 mr-1" />
                {t('proPack')}
              </Button>
            </Link>
            <div className="ml-2">{languageSelector}</div>
          </nav>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-orange-50 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMenuOpen ? <X className="h-6 w-6 text-gray-700" /> : <Menu className="h-6 w-6 text-gray-700" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 space-y-2 border-t border-gray-200 flex flex-col">
            {navItem('/', t('home'))}
            {navItem('/exercises', t('exercises'))}
            {navItem('/favorites', t('favorites'), <Heart className="h-4 w-4 mr-1" />)}
            <Link to="/pricing" onClick={() => setIsMenuOpen(false)}>
              <Button className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold">
                <Crown className="h-4 w-4 mr-1" />
                {t('proPack')}
              </Button>
            </Link>
            <div className="pt-2">{languageSelector}</div>
          </div>
        )}
      </div>
    </header>
  );
};
